import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SPECIALTIES = [
  'GP (General Practitioner)',
  'Neurologist',
  'Cardiologist',
  'Dermatologist',
  'Orthopaedic Surgeon',
  'Gastroenterologist',
  'Endocrinologist',
  'Rheumatologist',
  'Pulmonologist',
  'Psychiatrist',
  'Oncologist',
  'Urologist',
  'Gynaecologist',
  'Ophthalmologist',
  'ENT Specialist',
  'Other'
];

const AppointmentForm = ({ onSubmit, onCancel, initialData, prefillNotes, prefillDate, isSubmitting }) => {
  const [formData, setFormData] = useState({
    doctorName: '',
    specialty: '',
    appointmentDatetime: '',
    location: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        doctorName: initialData?.doctorName || '',
        specialty: initialData?.specialty || '',
        appointmentDatetime: initialData?.appointmentDatetime || '',
        location: initialData?.location || '',
        notes: initialData?.notes || ''
      });
    } else {
      // Pre-fill date if provided from calendar click
      const defaultDatetime = prefillDate ? `${prefillDate}T09:00` : '';
      setFormData(prev => ({
        ...prev,
        appointmentDatetime: defaultDatetime,
        notes: prefillNotes || ''
      }));
    }
  }, [initialData, prefillNotes, prefillDate]);

  const validate = () => {
    const newErrors = {};
    if (!formData?.doctorName?.trim()) newErrors.doctorName = 'Doctor name is required';
    if (!formData?.specialty) newErrors.specialty = 'Please select a specialty';
    if (!formData?.appointmentDatetime) {
      newErrors.appointmentDatetime = 'Appointment date and time is required';
    } else {
      const apptDate = new Date(formData.appointmentDatetime);
      if (apptDate <= new Date()) newErrors.appointmentDatetime = 'Appointment must be in the future';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validate()) onSubmit(formData);
  };

  const minDatetime = new Date(Date.now() + 60000)?.toISOString()?.slice(0, 16);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Doctor Name <span className="text-error">*</span>
        </label>
        <Input
          type="text"
          value={formData?.doctorName}
          onChange={(e) => handleChange('doctorName', e?.target?.value)}
          placeholder="e.g. Dr. Sarah Johnson"
          className={errors?.doctorName ? 'border-error' : ''}
        />
        {errors?.doctorName && <p className="text-xs text-error mt-1">{errors?.doctorName}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Specialty <span className="text-error">*</span>
        </label>
        <select
          value={formData?.specialty}
          onChange={(e) => handleChange('specialty', e?.target?.value)}
          className={`w-full px-3 py-2.5 rounded-lg border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${errors?.specialty ? 'border-error' : 'border-border'}`}
        >
          <option value="">Select specialty...</option>
          {SPECIALTIES?.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {errors?.specialty && <p className="text-xs text-error mt-1">{errors?.specialty}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Date &amp; Time <span className="text-error">*</span>
        </label>
        <input
          type="datetime-local"
          value={formData?.appointmentDatetime}
          min={minDatetime}
          onChange={(e) => handleChange('appointmentDatetime', e?.target?.value)}
          className={`w-full px-3 py-2.5 rounded-lg border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${errors?.appointmentDatetime ? 'border-error' : 'border-border'}`}
        />
        {errors?.appointmentDatetime && <p className="text-xs text-error mt-1">{errors?.appointmentDatetime}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Location / Clinic Name</label>
        <Input
          type="text"
          value={formData?.location}
          onChange={(e) => handleChange('location', e?.target?.value)}
          placeholder="e.g. City Medical Centre, Room 4"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Notes
          <span className="text-xs text-muted-foreground font-normal ml-2">(pre-filled from your latest check-in)</span>
        </label>
        <textarea
          value={formData?.notes}
          onChange={(e) => handleChange('notes', e?.target?.value)}
          placeholder="What would you like to discuss with your doctor?"
          rows={3}
          className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors resize-none"
        />
      </div>
      <div className="flex items-start gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
        <Icon name="Bell" size={16} className="text-primary flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          You will receive email reminders <strong>7 days</strong>, <strong>1 day</strong>, and <strong>2 hours</strong> before your appointment.
        </p>
      </div>
      <div className="flex gap-3 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={isSubmitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="default" className="flex-1" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : (initialData ? 'Update Appointment' : 'Add Appointment')}
        </Button>
      </div>
    </form>
  );
};

export default AppointmentForm;
